import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  VideoCameraIcon,
  FilmIcon,
  LanguageIcon,
  ArrowTopRightOnSquareIcon,
  CloudArrowUpIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  TvIcon,
  LinkIcon,
  ScissorsIcon,
  SparklesIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { videoApi, VideoTranslationJob, RawVideoUpload, YouTubeUploadEntry } from '@/services/videoApi';
import { youtubeApi, YouTubeUpload } from '@/services/youtubeApi';
import YouTubePublishModal from '@/components/Video/YouTubePublishModal';
import SocialPublishModal from '@/components/Video/SocialPublishModal';
import UploadVideoModal from '@/components/Video/UploadVideoModal';
import VideoDetailsDrawer from '@/components/Video/VideoDetailsDrawer';
import Pagination, { paginate } from '@/components/Common/Pagination';
import ViewToggle, { ViewMode } from '@/components/Common/ViewToggle';
import ActionMenu, { ActionMenuItem } from '@/components/Common/ActionMenu';
import { useAuth } from '@/hooks/useAuth';

const ITEMS_PER_PAGE = 9;

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    case 'failed':
      return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
    case 'processing':
      return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
    case 'pending':
      return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    default:
      return <ClockIcon className="h-5 w-5 text-gray-500" />;
  }
};

const VideosPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const [activeTab, setActiveTab] = useState<'uploaded' | 'translated' | 'published'>('uploaded');
  const [search, setSearch] = useState('');
  const [publishSource, setPublishSource] = useState<{
    type: 'translation' | 'upload';
    id: string;
    title: string;
    existingUploads: YouTubeUploadEntry[];
  } | null>(null);
  const [socialPublishSource, setSocialPublishSource] = useState<{ id: string; title: string } | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState<{ type: 'upload' | 'translation'; id: string } | null>(null);
  const [editingUpload, setEditingUpload] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editingJob, setEditingJob] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [publishedViewMode, setPublishedViewMode] = useState<ViewMode>('card');
  const [videoViewMode, setVideoViewMode] = useState<ViewMode>('card');
  const [uploadedPage, setUploadedPage] = useState(1);
  const [translatedPage, setTranslatedPage] = useState(1);
  const [publishedPage, setPublishedPage] = useState(1);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const bgPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopBgPolling = useCallback(() => {
    if (bgPollRef.current) { clearInterval(bgPollRef.current); bgPollRef.current = null; }
  }, []);

  useEffect(() => () => stopBgPolling(), [stopBgPolling]);

  const startBgPolling = useCallback((publishJobId: string) => {
    stopBgPolling();
    bgPollRef.current = setInterval(async () => {
      try {
        const { job } = await youtubeApi.pollPublishJob(publishJobId);
        if (job.status === 'completed') {
          stopBgPolling();
          queryClient.invalidateQueries({ queryKey: ['youtube-uploads'] });
          queryClient.invalidateQueries({ queryKey: ['video-uploads'] });
          queryClient.invalidateQueries({ queryKey: ['video-jobs'] });
          setPublishingId(null);
          toast.success(
            (t) => (
              <span className="flex items-center gap-2">
                Video published to YouTube!
                {job.videoUrl && (
                  <a href={job.videoUrl} target="_blank" rel="noopener noreferrer"
                    className="underline font-medium" onClick={() => toast.dismiss(t.id)}>
                    View
                  </a>
                )}
              </span>
            ),
            { duration: 6000 }
          );
        } else if (job.status === 'failed') {
          stopBgPolling();
          setPublishingId(null);
          toast.error(job.error || 'YouTube publish failed');
        }
      } catch {
        // transient — keep retrying
      }
    }, 4000);
  }, [stopBgPolling, queryClient]);

  const renameMutation = useMutation({
    mutationFn: ({ fileId, originalName }: { fileId: string; originalName: string }) =>
      videoApi.renameUpload(fileId, originalName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['video-uploads'] });
      toast.success('Video renamed successfully');
      setEditingUpload(null);
      setEditName('');
    },
    onError: () => toast.error('Failed to rename video'),
  });

  const updateTitleMutation = useMutation({
    mutationFn: ({ jobId, title }: { jobId: string; title: string }) => videoApi.updateJobTitle(jobId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['video-jobs'] });
      toast.success('Title updated successfully');
      setEditingJob(null);
      setEditTitle('');
    },
    onError: () => toast.error('Failed to update title'),
  });

  const { data: uploadsData, isLoading: uploadsLoading } = useQuery({
    queryKey: ['video-uploads'],
    queryFn: () => videoApi.getUploads(),
  });

  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['video-jobs'],
    queryFn: () => videoApi.getJobs({ limit: 100 }),
  });

  const { data: configData } = useQuery({
    queryKey: ['youtube-config'],
    queryFn: () => youtubeApi.getConfig(),
  });

  const { data: youtubeUploadsData, isLoading: youtubeUploadsLoading } = useQuery({
    queryKey: ['youtube-uploads'],
    queryFn: () => youtubeApi.getUploads(),
  });

  const uploads = uploadsData?.uploads || [];
  const jobs = (jobsData?.jobs || []) as VideoTranslationJob[];
  const youtubeUploads = youtubeUploadsData?.uploads || [];

  const filteredUploads = uploads.filter((upload: RawVideoUpload) =>
    upload.originalName.toLowerCase().includes(search.toLowerCase())
  );

  const filteredJobs = jobs.filter((job: VideoTranslationJob) =>
    (job.title || job.id).toLowerCase().includes(search.toLowerCase())
  );

  const filteredYoutubeUploads = youtubeUploads.filter((upload: YouTubeUpload) =>
    upload.title.toLowerCase().includes(search.toLowerCase()) ||
    upload.channelTitle?.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedUploads = paginate(filteredUploads, uploadedPage, ITEMS_PER_PAGE);
  const paginatedJobs = paginate(filteredJobs, translatedPage, ITEMS_PER_PAGE);
  const paginatedYoutubeUploads = paginate(filteredYoutubeUploads, publishedPage, ITEMS_PER_PAGE);

  useEffect(() => {
    setUploadedPage(1);
    setTranslatedPage(1);
    setPublishedPage(1);
  }, [search]);

  const youtubeEnabled = configData?.enabled && configData?.globallyConfigured;

  const handleTranslate = (upload: RawVideoUpload) => {
    navigate('/app/audio/video-translation', {
      state: {
        fileId: upload.fileId,
        fileName: upload.originalName,
        fileSize: upload.size,
      },
    });
  };

  const downloadUpload = async (fileId: string) => {
    try {
      await videoApi.downloadUpload(fileId);
    } catch {
      toast.error('Failed to download video');
    }
  };

  const downloadJob = async (job: VideoTranslationJob) => {
    const finalVideoUrl = job.result?.finalVideoUrl;
    if (!finalVideoUrl) {
      toast.error('Translated video is not available for download yet');
      return;
    }
    try {
      const ext = finalVideoUrl.split('.').pop() || 'mp4';
      await videoApi.downloadFinalVideo(job.id, finalVideoUrl, `${job.title || 'translated-video'}-${job.id}.${ext}`);
    } catch {
      toast.error('Failed to download video');
    }
  };

  const copyVideoUrl = async (type: 'upload' | 'translation', id: string) => {
    try {
      const { url } = type === 'upload'
        ? await videoApi.getUploadShareableUrl(id)
        : await videoApi.getJobShareableUrl(id);
      await navigator.clipboard.writeText(url);
      toast.success('Video URL copied to clipboard');
    } catch {
      toast.error('Failed to copy video URL');
    }
  };

  const generateClipsFromUpload = async (upload: RawVideoUpload) => {
    try {
      const { url } = await videoApi.getUploadShareableUrl(upload.fileId);
      const params = new URLSearchParams({
        videoS3Url: url,
        title: upload.originalName,
      });
      navigate(`/app/clips?${params.toString()}`);
    } catch {
      toast.error('Failed to get video URL for clip generation');
    }
  };

  const generateClipsFromJob = async (job: VideoTranslationJob) => {
    try {
      const { url } = await videoApi.getJobShareableUrl(job.id);
      const params = new URLSearchParams({
        videoS3Url: url,
        title: job.title || `Translation ${job.id}`,
        jobId: job.id,
      });
      navigate(`/app/clips?${params.toString()}`);
    } catch {
      toast.error('Failed to get video URL for clip generation');
    }
  };

  const understandUpload = async (upload: RawVideoUpload) => {
    try {
      const { url } = await videoApi.getUploadShareableUrl(upload.fileId);
      const params = new URLSearchParams({
        videoS3Url: url,
        title: upload.originalName,
      });
      navigate(`/app/video-understanding?${params.toString()}`);
    } catch {
      toast.error('Failed to get video URL for video understanding');
    }
  };

  const understandJob = async (job: VideoTranslationJob) => {
    try {
      const { url } = await videoApi.getJobShareableUrl(job.id);
      const params = new URLSearchParams({
        videoS3Url: url,
        title: job.title || `Translation ${job.id}`,
      });
      navigate(`/app/video-understanding?${params.toString()}`);
    } catch {
      toast.error('Failed to get video URL for video understanding');
    }
  };

  const buildUploadActions = (upload: RawVideoUpload): ActionMenuItem[] => [
    {
      key: 'download',
      label: 'Download',
      icon: ArrowDownTrayIcon,
      onClick: () => downloadUpload(upload.fileId),
    },
    {
      key: 'details',
      label: 'View details',
      icon: EyeIcon,
      onClick: () => setDetailsTarget({ type: 'upload', id: upload.fileId }),
    },
    {
      key: 'copy-url',
      label: 'Copy S3 URL',
      icon: LinkIcon,
      onClick: () => copyVideoUrl('upload', upload.fileId),
      hidden: !isSuperAdmin,
    },
    {
      key: 'publish-youtube',
      label: publishingId === upload.fileId ? 'Publishing…' : 'Publish to YouTube',
      icon: VideoCameraIcon,
      onClick: () =>
        setPublishSource({
          type: 'upload',
          id: upload.fileId,
          title: upload.originalName,
          existingUploads: upload.youtubeUploads || [],
        }),
      disabled: !youtubeEnabled || publishingId === upload.fileId,
      tone: 'danger',
      title: !youtubeEnabled ? 'YouTube integration not enabled' : undefined,
    },
    {
      key: 'publish-meta',
      label: 'Publish to Meta',
      icon: CloudArrowUpIcon,
      onClick: () => setSocialPublishSource({ id: upload.fileId, title: upload.originalName }),
      tone: 'brand',
      title: 'Publish to Facebook, Instagram, or WhatsApp',
    },
    {
      key: 'generate-clips',
      label: 'Generate Clips',
      icon: ScissorsIcon,
      onClick: () => generateClipsFromUpload(upload),
    },
    {
      key: 'understand',
      label: 'Understand video',
      icon: SparklesIcon,
      onClick: () => understandUpload(upload),
      title: 'Get transcript, chapters, topics, summary & highlights',
    },
    {
      key: 'translate',
      label: 'Translate',
      icon: PlayIcon,
      onClick: () => handleTranslate(upload),
    },
  ];

  const buildJobActions = (job: VideoTranslationJob): ActionMenuItem[] => {
    const completed = job.status === 'completed' && !!job.result?.finalVideoUrl;
    return [
      {
        key: 'download',
        label: 'Download',
        icon: ArrowDownTrayIcon,
        onClick: () => downloadJob(job),
        hidden: !completed,
      },
      {
        key: 'view',
        label: 'View',
        icon: ArrowTopRightOnSquareIcon,
        onClick: () => navigate(`/app/audio/video-translation?job=${job.id}`),
      },
      {
        key: 'details',
        label: 'View details',
        icon: EyeIcon,
        onClick: () => setDetailsTarget({ type: 'translation', id: job.id }),
      },
      {
        key: 'generate-clips',
        label: 'Generate Clips',
        icon: ScissorsIcon,
        onClick: () => generateClipsFromJob(job),
        hidden: !completed,
      },
      {
        key: 'understand',
        label: 'Understand video',
        icon: SparklesIcon,
        onClick: () => understandJob(job),
        hidden: !completed,
        title: 'Get transcript, chapters, topics, summary & highlights',
      },
      {
        key: 'copy-url',
        label: 'Copy S3 URL',
        icon: LinkIcon,
        onClick: () => copyVideoUrl('translation', job.id),
        hidden: !completed || !isSuperAdmin,
      },
      {
        key: 'publish-youtube',
        label: publishingId === job.id ? 'Publishing…' : 'Publish to YouTube',
        icon: VideoCameraIcon,
        onClick: () =>
          setPublishSource({
            type: 'translation',
            id: job.id,
            title: job.title || `Translation ${job.id}`,
            existingUploads: job.youtubeUploads || [],
          }),
        disabled: !youtubeEnabled || publishingId === job.id,
        hidden: !completed,
        tone: 'danger',
        title: !youtubeEnabled ? 'YouTube integration not enabled' : undefined,
      },
    ];
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FilmIcon className="h-7 w-7 text-indigo-600" />
            Videos
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage uploaded and translated videos
          </p>
        </div>
        <button
          type="button"
          onClick={() => setUploadModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <CloudArrowUpIcon className="h-4 w-4 mr-2" />
          Upload Video
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('uploaded')}
              className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'uploaded'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <CloudArrowUpIcon className="h-4 w-4" />
              Uploaded Videos
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-0.5 px-2 rounded-full text-xs">
                {uploads.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('translated')}
              className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'translated'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <LanguageIcon className="h-4 w-4" />
              Translated Videos
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-0.5 px-2 rounded-full text-xs">
                {jobs.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('published')}
              className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'published'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TvIcon className="h-4 w-4" />
              Published YouTube Videos
              <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-0.5 px-2 rounded-full text-xs">
                {youtubeUploads.length}
              </span>
            </button>
          </nav>
        </div>

        <div className="p-4">
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search videos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          {(activeTab === 'uploaded' || activeTab === 'translated') && (
            <div className="flex items-center justify-end mb-3">
              <ViewToggle value={videoViewMode} onChange={setVideoViewMode} />
            </div>
          )}

          {activeTab === 'uploaded' && (
            <div>
              {uploadsLoading ? (
                <div className="py-8 text-center text-gray-500">Loading uploads...</div>
              ) : filteredUploads.length === 0 ? (
                <div className="py-12 text-center">
                  <CloudArrowUpIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No uploaded videos found</p>
                  <button
                    onClick={() => setUploadModalOpen(true)}
                    className="mt-3 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                  >
                    Upload a video
                  </button>
                </div>
              ) : videoViewMode === 'card' ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {paginatedUploads.map((upload: RawVideoUpload) => (
                      <div
                        key={upload.fileId}
                        onClick={() => setDetailsTarget({ type: 'upload', id: upload.fileId })}
                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl flex flex-col cursor-pointer hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition-shadow"
                      >
                        <div className="relative aspect-video bg-gray-100 dark:bg-gray-700 overflow-hidden shrink-0 rounded-t-xl">
                          {upload.thumbnailUrl && (
                            <img
                              src={upload.thumbnailUrl}
                              alt={upload.originalName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                                (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex';
                              }}
                            />
                          )}
                          <div className={`${upload.thumbnailUrl ? 'hidden' : 'flex'} absolute inset-0 items-center justify-center bg-gray-100 dark:bg-gray-700`}>
                            <VideoCameraIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                          </div>
                        </div>
                        <div className="p-4 flex flex-col gap-3 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="min-w-0">
                              {editingUpload === upload.fileId ? (
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        renameMutation.mutate({ fileId: upload.fileId, originalName: editName });
                                      } else if (e.key === 'Escape') {
                                        setEditingUpload(null);
                                        setEditName('');
                                      }
                                    }}
                                    className="block w-36 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => renameMutation.mutate({ fileId: upload.fileId, originalName: editName })}
                                    className="p-1 text-green-600 hover:text-green-700"
                                    title="Save"
                                  >
                                    <CheckIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => { setEditingUpload(null); setEditName(''); }}
                                    className="p-1 text-red-600 hover:text-red-700"
                                    title="Cancel"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 min-w-0">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {upload.originalName}
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setEditingUpload(upload.fileId); setEditName(upload.originalName); }}
                                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
                                    title="Rename"
                                  >
                                    <PencilIcon className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {formatBytes(upload.size)} · {new Date(upload.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          {(upload.youtubeUploads || []).length > 0 && (
                            <a
                              href={(upload.youtubeUploads[upload.youtubeUploads.length - 1] as YouTubeUploadEntry).videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                            >
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                              Published
                            </a>
                          )}
                        </div>
                        <div
                          className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-700"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ActionMenu actions={buildUploadActions(upload)} />
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pagination
                    currentPage={uploadedPage}
                    totalPages={Math.ceil(filteredUploads.length / ITEMS_PER_PAGE)}
                    onPageChange={setUploadedPage}
                    totalItems={filteredUploads.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                  />
                </>
              ) : (
                <>
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Size</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Uploaded</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">YouTube Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedUploads.map((upload: RawVideoUpload) => (
                      <tr
                        key={upload.fileId}
                        onClick={() => setDetailsTarget({ type: 'upload', id: upload.fileId })}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <VideoCameraIcon className="h-5 w-5 text-gray-400 mr-2" />
                            {editingUpload === upload.fileId ? (
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      renameMutation.mutate({ fileId: upload.fileId, originalName: editName });
                                    } else if (e.key === 'Escape') {
                                      setEditingUpload(null);
                                      setEditName('');
                                    }
                                  }}
                                  className="block w-48 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  autoFocus
                                />
                                <button
                                  onClick={() => renameMutation.mutate({ fileId: upload.fileId, originalName: editName })}
                                  className="p-1 text-green-600 hover:text-green-700"
                                  title="Save"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => { setEditingUpload(null); setEditName(''); }}
                                  className="p-1 text-red-600 hover:text-red-700"
                                  title="Cancel"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">
                                  {upload.originalName}
                                </span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingUpload(upload.fileId); setEditName(upload.originalName); }}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                  title="Rename"
                                >
                                  <PencilIcon className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {formatBytes(upload.size)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(upload.uploadedAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          {(upload.youtubeUploads || []).length > 0 ? (
                            <a
                              href={(upload.youtubeUploads[upload.youtubeUploads.length - 1] as YouTubeUploadEntry).videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                            >
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                              Published
                            </a>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-gray-400 dark:text-gray-500">
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end">
                            <ActionMenu actions={buildUploadActions(upload)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <Pagination
                  currentPage={uploadedPage}
                  totalPages={Math.ceil(filteredUploads.length / ITEMS_PER_PAGE)}
                  onPageChange={setUploadedPage}
                  totalItems={filteredUploads.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
                </>
              )}
            </div>
          )}

          {activeTab === 'published' && (
            <div>
              {youtubeUploadsLoading ? (
                <div className="py-8 text-center text-gray-500">Loading published videos...</div>
              ) : filteredYoutubeUploads.length === 0 ? (
                <div className="py-12 text-center">
                  <TvIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No YouTube videos published yet</p>
                </div>
              ) : (
                <>
                  {/* View toggle */}
                  <div className="flex items-center justify-end mb-4">
                    <ViewToggle value={publishedViewMode} onChange={setPublishedViewMode} />
                  </div>

                  {/* Card view */}
                  {publishedViewMode === 'card' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {paginatedYoutubeUploads.map((upload) => (
                        <a
                          key={upload.videoId}
                          href={upload.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition-all"
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-video bg-gray-100 dark:bg-gray-700 overflow-hidden">
                            <img
                              src={`https://img.youtube.com/vi/${upload.videoId}/mqdefault.jpg`}
                              alt={upload.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                                (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex';
                              }}
                            />
                            <div className="hidden absolute inset-0 items-center justify-center bg-gray-100 dark:bg-gray-700">
                              <TvIcon className="h-12 w-12 text-gray-300 dark:text-gray-500" />
                            </div>
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 dark:bg-gray-900/90 rounded-full p-2">
                                <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                              </div>
                            </div>
                            <span className={`absolute top-2 right-2 text-xs px-1.5 py-0.5 rounded font-medium capitalize ${
                              upload.privacyStatus === 'public'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/80 dark:text-green-300'
                                : upload.privacyStatus === 'unlisted'
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/80 dark:text-yellow-300'
                                : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {upload.privacyStatus}
                            </span>
                          </div>
                          {/* Card body */}
                          <div className="p-3">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                              {upload.title}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                              {upload.channelTitle || 'Unknown channel'}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {new Date(upload.publishedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}

                  {/* List view */}
                  {publishedViewMode === 'list' && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Title</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Channel</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Privacy</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Published</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {paginatedYoutubeUploads.map((upload) => (
                            <tr key={upload.videoId} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="flex-shrink-0 w-16 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
                                    <img
                                      src={`https://img.youtube.com/vi/${upload.videoId}/mqdefault.jpg`}
                                      alt={upload.title}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                                        (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex';
                                      }}
                                    />
                                    <div className="hidden w-full h-full items-center justify-center">
                                      <TvIcon className="h-5 w-5 text-gray-300 dark:text-gray-500" />
                                    </div>
                                  </div>
                                  <a
                                    href={upload.videoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 truncate max-w-xs"
                                  >
                                    {upload.title}
                                  </a>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {upload.channelTitle || 'Unknown'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 capitalize">
                                {upload.privacyStatus}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {new Date(upload.publishedAt).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <a
                                  href={upload.videoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1.5 text-indigo-600 hover:text-indigo-700 text-xs font-medium"
                                >
                                  <ArrowTopRightOnSquareIcon className="h-3 w-3 mr-1" />
                                  View on YouTube
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <Pagination
                    currentPage={publishedPage}
                    totalPages={Math.ceil(filteredYoutubeUploads.length / ITEMS_PER_PAGE)}
                    onPageChange={setPublishedPage}
                    totalItems={filteredYoutubeUploads.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                  />
                </>
              )}
            </div>
          )}

          {activeTab === 'translated' && (
            <div>
              {jobsLoading ? (
                <div className="py-8 text-center text-gray-500">Loading translations...</div>
              ) : filteredJobs.length === 0 ? (
                <div className="py-12 text-center">
                  <LanguageIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No translated videos yet</p>
                </div>
              ) : videoViewMode === 'card' ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {paginatedJobs.map((job: VideoTranslationJob) => (
                      <div
                        key={job.id}
                        onClick={() => setDetailsTarget({ type: 'translation', id: job.id })}
                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl flex flex-col cursor-pointer hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition-shadow"
                      >
                        <div className="relative aspect-video bg-gray-100 dark:bg-gray-700 overflow-hidden shrink-0 rounded-t-xl">
                          {job.thumbnailUrl && (
                            <img
                              src={job.thumbnailUrl}
                              alt={job.title || `Translation ${job.id}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                                (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex';
                              }}
                            />
                          )}
                          <div className={`${job.thumbnailUrl ? 'hidden' : 'flex'} absolute inset-0 items-center justify-center bg-gray-100 dark:bg-gray-700`}>
                            <FilmIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                          </div>
                        </div>
                        <div className="p-4 flex flex-col gap-3 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="min-w-0">
                              {editingJob === job.id ? (
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        updateTitleMutation.mutate({ jobId: job.id, title: editTitle });
                                      } else if (e.key === 'Escape') {
                                        setEditingJob(null);
                                        setEditTitle('');
                                      }
                                    }}
                                    className="block w-36 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    autoFocus
                                  />
                                  <button onClick={() => updateTitleMutation.mutate({ jobId: job.id, title: editTitle })} className="p-1 text-green-600 hover:text-green-700" title="Save">
                                    <CheckIcon className="h-4 w-4" />
                                  </button>
                                  <button onClick={() => { setEditingJob(null); setEditTitle(''); }} className="p-1 text-red-600 hover:text-red-700" title="Cancel">
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 min-w-0">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {job.title || `Translation ${job.id}`}
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setEditingJob(job.id); setEditTitle(job.title || `Translation ${job.id}`); }}
                                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0"
                                    title="Edit title"
                                  >
                                    <PencilIcon className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {statusIcon(job.status)}
                                <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{job.status}</span>
                                <span className="text-xs text-gray-400 dark:text-gray-500">· {job.progress}%</span>
                              </div>
                            </div>
                          </div>
                          {(job.youtubeUploads || []).length > 0 && (
                            <a
                              href={(job.youtubeUploads[job.youtubeUploads.length - 1] as YouTubeUploadEntry).videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                            >
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                              Published
                            </a>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{new Date(job.createdAt).toLocaleDateString()}</p>
                        <div
                          className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-700"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ActionMenu actions={buildJobActions(job)} />
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pagination
                    currentPage={translatedPage}
                    totalPages={Math.ceil(filteredJobs.length / ITEMS_PER_PAGE)}
                    onPageChange={setTranslatedPage}
                    totalItems={filteredJobs.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                  />
                </>
              ) : (
                <>
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">YouTube Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Progress</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedJobs.map((job: VideoTranslationJob) => (
                      <tr
                        key={job.id}
                        onClick={() => setDetailsTarget({ type: 'translation', id: job.id })}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <FilmIcon className="h-5 w-5 text-gray-400 mr-2" />
                            {editingJob === job.id ? (
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={editTitle}
                                  onChange={(e) => setEditTitle(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      updateTitleMutation.mutate({ jobId: job.id, title: editTitle });
                                    } else if (e.key === 'Escape') {
                                      setEditingJob(null);
                                      setEditTitle('');
                                    }
                                  }}
                                  className="block w-48 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                  autoFocus
                                />
                                <button
                                  onClick={() => updateTitleMutation.mutate({ jobId: job.id, title: editTitle })}
                                  className="p-1 text-green-600 hover:text-green-700"
                                  title="Save"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => { setEditingJob(null); setEditTitle(''); }}
                                  className="p-1 text-red-600 hover:text-red-700"
                                  title="Cancel"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">
                                  {job.title || `Translation ${job.id}`}
                                </span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingJob(job.id); setEditTitle(job.title || `Translation ${job.id}`); }}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                  title="Edit title"
                                >
                                  <PencilIcon className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {statusIcon(job.status)}
                            <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{job.status}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(job.youtubeUploads || []).length > 0 ? (
                            <a
                              href={(job.youtubeUploads[job.youtubeUploads.length - 1] as YouTubeUploadEntry).videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                            >
                              <CheckCircleIcon className="h-3.5 w-3.5" />
                              Published
                            </a>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-gray-400 dark:text-gray-500">
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {job.progress}%
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(job.createdAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end">
                            <ActionMenu actions={buildJobActions(job)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <Pagination
                  currentPage={translatedPage}
                  totalPages={Math.ceil(filteredJobs.length / ITEMS_PER_PAGE)}
                  onPageChange={setTranslatedPage}
                  totalItems={filteredJobs.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {uploadModalOpen && (
        <UploadVideoModal
          onClose={() => setUploadModalOpen(false)}
          onUploaded={() => {
            queryClient.invalidateQueries({ queryKey: ['video-uploads'] });
            setUploadModalOpen(false);
          }}
        />
      )}

      {detailsTarget && (
        <VideoDetailsDrawer
          type={detailsTarget.type}
          id={detailsTarget.id}
          onClose={() => setDetailsTarget(null)}
        />
      )}

      {socialPublishSource && (
        <SocialPublishModal
          sourceType="upload"
          sourceId={socialPublishSource.id}
          defaultCaption={socialPublishSource.title}
          onClose={() => setSocialPublishSource(null)}
          onPublished={() => setSocialPublishSource(null)}
        />
      )}

      {publishSource && (
        <YouTubePublishModal
          sourceType={publishSource.type}
          sourceId={publishSource.id}
          defaultTitle={publishSource.title}
          existingUploads={publishSource.existingUploads}
          onClose={() => setPublishSource(null)}
          onPublishStart={() => setPublishingId(publishSource.id)}
          onPublishJobQueued={(publishJobId) => {
            startBgPolling(publishJobId);
          }}
          onPublished={() => {
            stopBgPolling();
            queryClient.invalidateQueries({ queryKey: ['youtube-uploads'] });
            queryClient.invalidateQueries({ queryKey: ['video-uploads'] });
            queryClient.invalidateQueries({ queryKey: ['video-jobs'] });
            setPublishingId(null);
            setPublishSource(null);
          }}
          onPublishError={() => {
            stopBgPolling();
            setPublishingId(null);
          }}
        />
      )}
    </div>
  );
};

export default VideosPage;
